import type { CartDataFragment } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

export const useOrderValidation = ({
  wantedTime,
  dropoffLocationId,
  billingAccountId,
  cart,
}: CanSubmitOrderProps) => {
  // Wanted Time
  const hasWantedTime = Boolean(wantedTime);

  // Dropoff Locations
  const hasMultipleDropoffLocations =
    (cart?.restaurant?.availableDropOffLocations ?? []).length > 1;
  const isNotDelivery = cart?.orderType !== 'DELIVERY';
  const needsDropoffLocation = isNotDelivery && hasMultipleDropoffLocations;

  const isDropoffSelected =
    Boolean(dropoffLocationId) && dropoffLocationId !== 'placeholder';
  const hasDropoffSelectionIfNeeded = needsDropoffLocation
    ? isDropoffSelected
    : true;

  // Billing Account
  const hasBillingAccountSelected = billingAccountId !== 'ADD';

  // Submit Order Validations
  const shouldBlockCheckoutOnPress =
    !hasWantedTime ||
    !hasBillingAccountSelected ||
    !hasDropoffSelectionIfNeeded;
  const cannotSubmitOrderReason = useCannotSubmitOrderReason({
    hasWantedTime,
    hasDropoffSelectionIfNeeded,
    hasBillingAccountSelected,
    cart,
  });

  return {
    shouldBlockCheckoutOnPress,
    cannotSubmitOrderReason,
  };
};

const useCannotSubmitOrderReason = ({
  hasWantedTime,
  hasDropoffSelectionIfNeeded,
  hasBillingAccountSelected,
  cart,
}: CannotSubmitOrderReasonProps) => {
  const { t } = useLocalizationContext();

  if (!hasWantedTime) {
    return t('checkout.cannot-submit.wanted-time');
  }

  if (!hasDropoffSelectionIfNeeded) {
    return cart?.restaurant?.isOutpost
      ? t('checkout.cannot-submit.drop-off')
      : t('checkout.cannot-submit.pickup');
  }

  if (!hasBillingAccountSelected) {
    return t('checkout.cannot-submit.billing-account');
  }

  return t('general.error');
};

type CanSubmitOrderProps = {
  readonly wantedTime?: string | null;
  readonly dropoffLocationId?: string | null;
  readonly billingAccountId?: string | null;
  readonly cart?: Partial<CartDataFragment> | null;
};

type CannotSubmitOrderReasonProps = {
  readonly hasWantedTime: boolean;
  readonly hasDropoffSelectionIfNeeded: boolean;
  readonly hasBillingAccountSelected: boolean;
  readonly cart?: Partial<CartDataFragment> | null;
};
